import { useState, useEffect } from "react";
import { Save, Tag } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux";
import {
  createTemplate,
  updateTemplate,
} from "../../../store/features/templates/templatesActions";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../store/features/ui/uiActions";
import { t } from "i18next";


export function TemplateModal({ template, onClose }) {
  const dispatch = useAppDispatch();
  const { isCreating, isUpdating } = useAppSelector((state) => state.templates);

  const [formData, setFormData] = useState({
    event_type: "",
    media: "email",
    language: "en",
    subject: "",
    content: "",
  });

  const [placeholders, setPlaceholders] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (template) {
      setFormData({
        event_type: template.event_type,
        media: template.media,
        language: template.language,
        subject: template.subject || "",
        content: template.content,
      });
      setPlaceholders(template.placeholders);
    }
  }, [template]);

  // Extract placeholders from content
  useEffect(() => {
    const matches = formData.content.match(/\{([^}]+)\}/g);
    const extracted = matches ? matches.map((match) => match.slice(1, -1)) : [];
    setPlaceholders([...new Set(extracted)]);
  }, [formData.content]);

  // Auto-save functionality
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.content.trim()) {
        setLastSaved(new Date());
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const templateData = {
      ...formData,
      placeholders,
    };

    try {
      if (template) {
        await dispatch(updateTemplate(template.id, templateData));
        dispatch(showSuccessNotification("Template updated successfully"));
      } else {
        await dispatch(createTemplate(templateData));
        dispatch(showSuccessNotification("Template created successfully"));
      }
      onClose();
    } catch (error) {
      dispatch(showErrorNotification("Failed to save template"));
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "s") {
      e.preventDefault();
      handleSubmit(e);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-semibold">
              {template ? "Edit Template" : "New Message Template"}
            </h5>
            <div className="d-flex align-items-center gap-3">
              {lastSaved && (
                <small className="text-success save-indicator">
                  ✓ Saved {lastSaved.toLocaleTimeString()}
                </small>
              )}
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={isLoading}
              ></button>
            </div>
          </div>

          <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-medium">{t("hevent")}</label>
                  <input
                    type="text"
                    value={formData.event_type}
                    onChange={(e) =>
                      setFormData({ ...formData, event_type: e.target.value })
                    }
                    className="form-control"
                    placeholder="SELLER_DOC_REMINDER"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-medium">Media Type</label>
                  <select
                    value={formData.media}
                    onChange={(e) =>
                      setFormData({ ...formData, media: e.target.value })
                    }
                    className="form-select"
                    disabled={isLoading}
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="telegram">Telegram</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-medium">Language</label>
                  <select
                    value={formData.language}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        language: e.target.value,
                      })
                    }
                    className="form-select"
                    disabled={isLoading}
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                  </select>
                </div>

                {formData.media === "email" && (
                  <div className="col-12">
                    <label className="form-label fw-medium">Subject</label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      className="form-control"
                      placeholder="Email subject line"
                      disabled={isLoading}
                    />
                  </div>
                )}

                <div className="col-12">
                  <label className="form-label fw-medium">
                    Message Content
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) =>
                      setFormData({ ...formData, content: e.target.value })
                    }
                    rows={6}
                    className="form-control font-monospace"
                    placeholder="Hi {seller_name}, please upload your {doc_name}..."
                    required
                    disabled={isLoading}
                  />
                </div>

                {placeholders.length > 0 && (
                  <div className="col-12">
                    <label className="form-label fw-medium">
                      Detected Placeholders
                    </label>
                    <div className="d-flex flex-wrap gap-2">
                      {placeholders.map((placeholder) => (
                        <span key={placeholder} className="placeholder-chip">
                          <Tag size={12} />
                          {placeholder}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* <div className="col-12">
                  <div className="alert alert-info">
                    <h6 className="alert-heading fw-medium mb-2">
                      Keyboard Shortcuts
                    </h6>
                    <div className="small">
                      <div>⌘ + S: Save template</div>
                      <div>Esc: Close modal</div>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Template
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
